/* build: matched-pair-qty2 */
function openMittenProductDetail(details, id) {
  details.querySelectorAll('[data-mitten-detail]').forEach((item) => {
    const active = item.dataset.mittenDetail === id;
    const toggle = item.querySelector('[data-mitten-detail-toggle]');
    const panel = item.querySelector('[data-mitten-detail-panel]');

    if (item.tagName === 'DETAILS') {
      item.classList.remove('is-open');
      item.open = active;
    } else {
      item.classList.toggle('is-open', active);
      if (toggle) toggle.setAttribute('aria-expanded', String(active));
      if (panel) panel.hidden = !active;
    }
  });
}

class ArthurMittenBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-mitten-form]');
    this.productData = this.readProductData();
    this.groups = Array.from(this.querySelectorAll('[data-option-group]'));
    this.optionButtons = Array.from(this.querySelectorAll('[data-mitten-option]'));
    this.submitButton = this.querySelector('[data-mitten-submit]');
    this.submitLabel = this.querySelector('[data-mitten-submit-label]');
    this.status = this.querySelector('[data-mitten-status]');
    this.mittens = Array.isArray(this.productData?.mittens) ? this.productData.mittens : [];
    this.mainImages = Array.from(this.querySelectorAll('[data-main-image]'));
    this.heroRequestIds = [0, 0];
    this.selections = [null, null];
    this.selectedPair = null;
    this.busy = false;
    this.controller = new AbortController();
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.labels = {
      select: this.dataset.addLabel || 'Select your pair',
      ready: this.dataset.readyLabel || 'Add pair to bag',
      adding: this.dataset.addingLabel || 'Adding...',
      added: this.dataset.addedLabel || 'Added to bag',
    };

    if (!this.form || !this.productData || !this.submitButton || !this.mittens.length) return;

    this.initializeDisclosureHoverGuard();

    const signal = this.controller.signal;
    this.initializeProductDetails(signal);
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectOption(button), { signal });
    });
    this.groups.forEach((group) => {
      const toggle = group.querySelector('[data-option-toggle]');
      if (toggle) {
        toggle.addEventListener('click', () => this.toggleGroup(group), { signal });
      }
    });
    this.querySelectorAll('[data-mitten-thumbnail]').forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => this.selectThumbnail(thumbnail), { signal });
    });
    this.form.addEventListener('submit', (event) => this.submit(event), { signal });

    this.initializeSelections();
    this.refresh();
    this.syncInitialHeroImages();
  }

  initializeDisclosureHoverGuard() {
    if (window.__pointerMovedAt !== undefined) return;

    window.__pointerMovedAt = 0;
    window.addEventListener(
      'mousemove',
      () => {
        window.__pointerMovedAt = Date.now();
      },
      { passive: true }
    );
  }

  initializeProductDetails(signal) {
    this.detailHoverTimer = null;

    this.addEventListener(
      'pointerover',
      (event) => {
        const item = event.target.closest('[data-mitten-detail]');
        if (!item || !this.contains(item) || item.contains(event.relatedTarget)) return;

        this.cancelDetailHover();
        if (event.pointerType !== 'mouse') return;
        if (!window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) return;
        if (Date.now() - (window.__pointerMovedAt || 0) >= 120) return;

        this.detailHoverTimer = window.setTimeout(
          () => this.openProductDetail(item.dataset.mittenDetail),
          250
        );
      },
      { signal }
    );
    this.addEventListener(
      'pointerout',
      (event) => {
        const item = event.target.closest('[data-mitten-detail]');
        if (!item || item.contains(event.relatedTarget)) return;
        this.cancelDetailHover();
      },
      { signal }
    );

    const initialItems = this.getProductDetailItems();
    const initialItem = initialItems.find(
      (item) => item.open || item.classList.contains('is-open')
    );
    if (initialItem) this.openProductDetail(initialItem.dataset.mittenDetail);
  }

  getProductDetailItems() {
    return Array.from(this.querySelectorAll('[data-mitten-detail]'));
  }

  cancelDetailHover() {
    window.clearTimeout(this.detailHoverTimer);
    this.detailHoverTimer = null;
  }

  openProductDetail(id) {
    const details = this.querySelector('[data-mitten-details]');
    if (details) openMittenProductDetail(details, id);
  }

  disconnectedCallback() {
    this.cancelDetailHover();
    if (this.controller) this.controller.abort();
  }

  readProductData() {
    const node = this.querySelector('[data-mitten-product]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      this.capture(error, 'product_json');
      return null;
    }
  }

  get disallowMatching() {
    return this.dataset.disallowMatching === 'true';
  }

  get shouldUpdateHero() {
    return this.dataset.updateHero === 'true';
  }

  get matchingPair() {
    return this.dataset.matchingPair === 'true';
  }

  mittenById(variantId) {
    if (!variantId) return null;
    return this.mittens.find((mitten) => mitten.id === Number(variantId)) || null;
  }

  // Section settings name a mitten by colourway or handle, whichever the merchant typed.
  findMitten(reference) {
    if (!reference) return null;
    const key = String(reference).trim().toLocaleLowerCase();
    return (
      this.mittens.find(
        (mitten) =>
          String(mitten.label).trim().toLocaleLowerCase() === key ||
          String(mitten.handle).trim().toLocaleLowerCase() === key
      ) || null
    );
  }

  initializeSelections() {
    const firstAvailable = this.mittens.find((mitten) => mitten.available);
    const one = this.findMitten(this.dataset.defaultOptionOne) || firstAvailable;
    if (!one) return;

    if (this.matchingPair) {
      this.selections = [one.id, one.id];
      return;
    }

    let two = this.findMitten(this.dataset.defaultOptionTwo);
    if (!two || (this.disallowMatching && two.id === one.id)) {
      two =
        this.mittens.find((mitten) => mitten.available && mitten.id !== one.id) ||
        (this.disallowMatching ? null : one);
    }

    this.selections = [one.id, two ? two.id : null];
  }

  syncInitialHeroImages() {
    if (!this.shouldUpdateHero) return;

    this.selections.forEach((variantId, index) => {
      const image = this.imageForSelection(variantId, index);
      if (image) this.setHeroImage(image, this.titleForSelection(variantId), index);
    });
  }

  // Prefer the rendered card's image so option_image block overrides win, and fall back to
  // the mitten's own featured image (the matching-pair layout has no second group).
  imageForSelection(variantId, index) {
    if (!variantId) return null;
    const button = this.optionButtons.find(
      (candidate) =>
        Number(candidate.dataset.optionIndex) === index &&
        Number(candidate.dataset.variantId) === Number(variantId)
    );
    return button?.dataset.optionImage || this.mittenById(variantId)?.image || null;
  }

  titleForSelection(variantId) {
    return this.mittenById(variantId)?.title || this.productData.title || '';
  }

  toggleGroup(group) {
    const index = Number(group.dataset.optionIndex);
    this.groups.forEach((candidate) => {
      const isTarget = candidate === group;
      candidate.classList.toggle('is-active', isTarget);
      if (this.mobileQuery.matches) {
        const shouldOpen = isTarget && !candidate.classList.contains('is-open');
        candidate.classList.toggle('is-open', shouldOpen);
        const toggle = candidate.querySelector('[data-option-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(shouldOpen));
      }
    });

    this.dispatchEvent(
      new CustomEvent('arthur:mitten:selector_open', {
        bubbles: true,
        detail: { optionIndex: index },
      })
    );
  }

  selectOption(button) {
    if (button.disabled || this.busy) return;

    const index = Number(button.dataset.optionIndex);
    const value = button.dataset.optionValue;
    const variantId = Number(button.dataset.variantId);
    this.selections[index] = variantId;
    if (this.matchingPair && index === 0) {
      this.selections[1] = variantId;
    }
    this.setStatus('');

    if (this.mobileQuery.matches) {
      const currentGroup = this.groups.find(
        (group) => Number(group.dataset.optionIndex) === index
      );
      const nextGroup = this.groups.find(
        (group) => Number(group.dataset.optionIndex) === index + 1
      );

      if (currentGroup) this.setGroupOpen(currentGroup, false);
      if (nextGroup) {
        this.groups.forEach((group) => group.classList.toggle('is-active', group === nextGroup));
        this.setGroupOpen(nextGroup, true);
      }
    } else {
      const nextIndex = index === 0 ? 1 : index;
      this.groups.forEach((group) => {
        group.classList.toggle('is-active', Number(group.dataset.optionIndex) === nextIndex);
      });
    }

    this.refresh(button.dataset.optionImage, index);
    if (this.matchingPair && button.dataset.optionImage) {
      this.setHeroImage(button.dataset.optionImage, this.titleForSelection(variantId), 1);
    }
    this.dispatchEvent(
      new CustomEvent('arthur:mitten:option_change', {
        bubbles: true,
        detail: { optionIndex: index, value },
      })
    );
  }

  setGroupOpen(group, open) {
    group.classList.toggle('is-open', open);
    const toggle = group.querySelector('[data-option-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  }

  refresh(optionImage, changedIndex) {
    this.optionButtons.forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const otherIndex = index === 0 ? 1 : 0;
      const variantId = Number(button.dataset.variantId);
      const mitten = this.mittenById(variantId);
      const selected = this.selections[index] === variantId;
      const matchingBlocked =
        this.disallowMatching && this.selections[otherIndex] === variantId;

      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      button.disabled = !selected && (matchingBlocked || !mitten || !mitten.available);
    });

    this.groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const selectedLabel = group.querySelector('[data-selected-label]');
      if (selectedLabel) {
        selectedLabel.textContent = this.mittenById(this.selections[index])?.label || '';
      }
    });

    this.selectedPair = this.findSelectedPair();
    this.submitButton.disabled = !this.selectedPair || this.busy;
    this.updateSubmitLabel();

    if (this.selectedPair) {
      this.updatePrice(this.selectedPair.reduce((total, mitten) => total + mitten.price, 0));
    }

    if (this.shouldUpdateHero && optionImage && Number.isInteger(changedIndex)) {
      this.setHeroImage(
        optionImage,
        this.titleForSelection(this.selections[changedIndex]),
        changedIndex
      );
    }
  }

  // Both gloves must resolve to an in-stock mitten before the pair can be added.
  findSelectedPair() {
    const [one, two] = this.selections.map((variantId) => this.mittenById(variantId));
    if (!one || !two) return null;
    if (this.disallowMatching && one.id === two.id) return null;
    if (!one.available || !two.available) return null;
    return [one, two];
  }

  updatePrice(price) {
    if (typeof window.formatMoney !== 'function') return;
    this.querySelectorAll('[data-mitten-price]').forEach((node) => {
      node.innerHTML = window.formatMoney(price, window.theme.moneyFormat);
    });
  }

  setHeroImage(src, alt, optionIndex = 0) {
    if (!src) return;
    const index = Number(optionIndex);
    const mainImage = this.mainImages.find(
      (image) => Number(image.dataset.optionIndex) === index
    );
    if (!mainImage) return;

    const nextSrc = this.imageUrl(src, 2400);
    const nextAlt = alt || this.productData.title || '';
    if (mainImage.src === nextSrc) {
      mainImage.alt = nextAlt;
      return;
    }

    const requestId = (this.heroRequestIds[index] || 0) + 1;
    this.heroRequestIds[index] = requestId;

    const preloader = new Image();
    preloader.decoding = 'async';

    let settled = false;
    const swapWhenReady = () => {
      if (settled) return;
      settled = true;
      if (this.heroRequestIds[index] !== requestId) return;
      this.fadeToHeroImage(mainImage, nextSrc, nextAlt, index, requestId);
    };

    preloader.addEventListener('load', swapWhenReady, { once: true });
    preloader.addEventListener(
      'error',
      () => {
        settled = true;
        if (this.heroRequestIds[index] === requestId) {
          mainImage.classList.remove('is-swapping');
        }
      },
      { once: true }
    );
    preloader.src = nextSrc;
    if (preloader.complete && preloader.naturalWidth > 0) swapWhenReady();
  }

  fadeToHeroImage(mainImage, nextSrc, nextAlt, index, requestId) {
    const applyImage = () => {
      if (this.heroRequestIds[index] !== requestId) {
        mainImage.classList.remove('is-swapping');
        return;
      }

      mainImage.removeAttribute('srcset');
      mainImage.src = nextSrc;
      mainImage.alt = nextAlt;

      const reveal = () => {
        if (this.heroRequestIds[index] !== requestId) return;
        requestAnimationFrame(() => mainImage.classList.remove('is-swapping'));
      };

      if (typeof mainImage.decode === 'function') {
        mainImage.decode().catch(() => {}).then(reveal);
      } else {
        reveal();
      }
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyImage();
      return;
    }

    let applied = false;
    const applyOnce = () => {
      if (applied) return;
      applied = true;
      mainImage.removeEventListener('transitionend', onTransitionEnd);
      applyImage();
    };
    const onTransitionEnd = (event) => {
      if (event.propertyName === 'opacity') applyOnce();
    };

    mainImage.addEventListener('transitionend', onTransitionEnd);
    mainImage.classList.add('is-swapping');
    window.setTimeout(applyOnce, 240);
  }

  imageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      // A URL carrying BOTH width and height is a deliberate crop box built in Liquid —
      // the split hero requests 1200x2400 with crop=left/right so each half holds exactly
      // one glove. Setting width alone squares the box back up, which silently disables
      // the crop and restores the full two-glove frame. Leave those URLs as authored;
      // they are already sized for the slot, and not inflating them keeps us clear of the
      // oversized-image memory crash on iOS.
      if (url.searchParams.get('width') && url.searchParams.get('height')) {
        return url.toString();
      }
      url.searchParams.set('width', width);
      return url.toString();
    } catch (_error) {
      return src;
    }
  }

  selectThumbnail(thumbnail) {
    this.querySelectorAll('[data-mitten-thumbnail]').forEach((candidate) => {
      candidate.classList.toggle('is-active', candidate === thumbnail);
    });
    this.setHeroImage(
      thumbnail.dataset.thumbnailImage,
      thumbnail.dataset.thumbnailAlt,
      Number(thumbnail.dataset.thumbnailSlot || 0)
    );
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy) return;

    if (!this.selections[0] || !this.selections[1]) {
      this.setStatus('Choose both mittens to continue.');
      this.openFirstMissingGroup();
      return;
    }

    if (!this.selectedPair) {
      this.setStatus('That pair is currently unavailable.');
      return;
    }

    this.setBusy(true);
    this.setStatus('');

    try {
      const [one, two] = this.selectedPair;
      // Tags both lines so the cart can present them as one pair later. The underscore
      // keeps it out of the customer-facing cart and checkout.
      const pairKey = `${Date.now().toString(36)}`;
      // Always one line per glove — Mitten 1 is the left hand, Mitten 2 the right. Recording
      // the hand lets the cart show the matching _L / _R photograph.
      //
      // Two gloves of the SAME colourway used to collapse into a single line at quantity 2.
      // Shopify was happy and the warehouse picked the right thing, but the cart read as
      // "1 pair" beside a quantity of 2, which looks like two pairs. Keeping them as two
      // lines of one makes the cart say exactly what it is: a left glove and a right glove.
      //
      // Two lines of the same variant would normally merge back into one. They stay apart
      // because their properties differ — '_hand' alone is enough.
      //
      // Underscore-prefixed properties stay out of the customer-facing cart and checkout;
      // 'Glove' is deliberately visible so the shopper can tell the two lines apart.
      // Mitten 2 is sent first on purpose: /cart/add.js inserts each new line above the
      // previous one, so posting right-then-left leaves the cart reading left, then right —
      // the same order as the two columns in the builder.
      const items = [
        { id: two.id, quantity: 1, properties: { Glove: 'Right', _pair: pairKey, _hand: 'right' } },
        { id: one.id, quantity: 1, properties: { Glove: 'Left', _pair: pairKey, _hand: 'left' } },
      ];

      const modalCart = window.theme.settings.cart_type === 'modal';
      const payload = { items };
      if (modalCart) {
        payload.sections = 'cart-items,cart-footer,cart-item-count';
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(this.cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add this pair.');
      }

      this.submitLabel.textContent = this.labels.added;
      this.setStatus(`${one.label} + ${two.label} added to bag.`, 4000);

      document.body.dispatchEvent(
        new CustomEvent('arthur:mitten:add_success', {
          bubbles: true,
          detail: {
            variantIds: [one.id, two.id],
            optionOne: one.label,
            optionTwo: two.label,
          },
        })
      );

      if (modalCart) {
        document.body.dispatchEvent(
          new CustomEvent('shapes:modalcart:afteradditem', {
            bubbles: true,
            detail: { response: data },
          })
        );
      } else {
        window.location.assign(window.theme.routes.cart_url);
        return;
      }

      window.setTimeout(() => {
        this.setBusy(false);
      }, 1800);
    } catch (error) {
      this.capture(error, 'cart_add');
      this.setStatus(error.message || 'Unable to add this pair. Please try again.');
      this.setBusy(false);
    }
  }

  openFirstMissingGroup() {
    const missingIndex = this.selections[0] ? 1 : 0;
    const group = this.groups.find(
      (candidate) => Number(candidate.dataset.optionIndex) === missingIndex
    );
    if (!group) return;

    this.groups.forEach((candidate) => {
      candidate.classList.toggle('is-active', candidate === group);
      if (this.mobileQuery.matches) this.setGroupOpen(candidate, candidate === group);
    });
  }

  // The multi-item payload needs the Ajax endpoint; theme routes give the plain path.
  cartAddUrl() {
    const url = window.theme.routes.cart_add_url || '/cart/add';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  setBusy(busy) {
    this.busy = busy;
    this.submitButton.disabled = busy || !this.selectedPair;
    this.submitButton.setAttribute('aria-busy', String(busy));
    if (busy) {
      this.submitLabel.textContent = this.labels.adding;
    } else {
      this.updateSubmitLabel();
    }
  }

  updateSubmitLabel() {
    if (!this.submitLabel || this.busy) return;
    const ready = Boolean(this.selectedPair);
    this.submitLabel.textContent = ready ? this.labels.ready : this.labels.select;
  }

  setStatus(message, autoClearMs = 0) {
    if (!this.status) return;
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
    this.status.textContent = message;
    // Confirmations are transient — leaving "added to bag" under the button makes it look
    // like unresolved state. Errors and prompts stay until the customer acts on them.
    if (message && autoClearMs) {
      this.statusTimer = setTimeout(() => {
        if (this.status.textContent === message) this.status.textContent = '';
        this.statusTimer = null;
      }, autoClearMs);
    }
  }

  capture(error, stage) {
    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.captureException(error, {
        tags: { feature: 'mitten-builder', stage },
      });
    }
  }
}

if (!customElements.get('mitten-builder')) {
  customElements.define('mitten-builder', ArthurMittenBuilder);
}
